'use client';

import {
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import {
  evaluateMaterialGraph,
  disposeMaterialGraphResult,
  graphTextureReadyForGpu,
  materialGraphUsesPerMeshUvRandomize,
  type AttributeDef,
  type EvalResult,
  type MaterialGraphData,
} from './material-graph-eval';
import { PrimitiveMaterial } from './primitive-material';
import { compileAsyncSafe, patchCompileAsyncOnRenderer } from './compile-async-safe';

const THUMB_TEX_SLOTS = [
  'map',
  'normalMap',
  'bumpMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'aoMap',
  'alphaMap',
  'displacementMap',
  'anisotropyMap',
  'clearcoatMap',
  'clearcoatRoughnessMap',
  'clearcoatNormalMap',
] as const;

export interface MaterialSphereThumbnailHandle {
  /** Renders a few frames, then returns a PNG blob (or null). Requires `preserveDrawingBuffer` on the GL context. */
  captureToBlob: (mimeType?: string, quality?: number) => Promise<Blob | null>;
}

export interface MaterialSphereThumbnailProps {
  type: 'pbr' | 'graph';
  data: unknown;
  /** Product attribute defs (for graph attributeInput / swatches). */
  attributeDefs?: AttributeDef[];
  /** Pixel size of the square canvas; omit to fill the parent (parent must have a defined size). */
  size?: number;
  className?: string;
  /**
   * When this string changes, the thumbnail schedules extra redraws (textures / env loading)
   * and restarts a short “warm-up” invalidation window.
   */
  renderKey?: string;
  /** When set, capture the preview to a blob this many ms after mount and report it via `onAutoCaptured`. */
  autoCaptureDelayMs?: number;
  /** Receives the captured blob (or null on failure) when `autoCaptureDelayMs` elapses. */
  onAutoCaptured?: (blob: Blob | null) => void;
}

function StudioPreview({
  overrides,
  fallback,
  sphereRef,
}: {
  overrides: Record<string, unknown> | null;
  fallback?: { color?: string; metalness?: number; roughness?: number };
  sphereRef: React.RefObject<THREE.Mesh | null>;
}) {
  return (
    <>
      <color attach="background" args={['#1a1a1e']} />
      <ambientLight intensity={0.45} />
      <hemisphereLight args={['#dfe7f2', '#2a2a30', 0.55]} />
      <directionalLight position={[2.8, 4.2, 2.2]} intensity={1.05} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[4, 4]} />
        <meshStandardMaterial color="#6e6e78" roughness={0.92} metalness={0.05} />
      </mesh>
      <mesh ref={sphereRef} position={[0, 0.42, 0]}>
        <sphereGeometry args={[0.42, 32, 32]} />
        <PrimitiveMaterial
          overrides={overrides as Record<string, any> | null}
          fallback={fallback}
        />
      </mesh>
    </>
  );
}

/** Demand frameloop: warm-up invalidation + extra frames while textures are still loading. */
function ThumbnailDemandDriver({
  sphereRef,
  bootKey,
}: {
  sphereRef: React.RefObject<THREE.Mesh | null>;
  bootKey: string;
}) {
  const invalidate = useThree((s) => s.invalidate);

  useEffect(() => {
    invalidate();
    // A few staggered one-shot redraws catch the environment map and other async
    // assets settling. The old steady 160ms×3.6s interval, multiplied across the
    // ~15 live preview canvases in the material shelf, showed up as repeated long
    // 'setInterval' handler violations and constant GPU churn. `useFrame` below
    // still invalidates while any texture slot is mid-load, so slow-loading assets
    // are not missed — these timers only cover the env/post settle window.
    const timers = [120, 360, 800, 1600, 2800].map((ms) =>
      setTimeout(() => invalidate(), ms),
    );
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [bootKey, invalidate]);

  useFrame(() => {
    const mesh = sphereRef.current;
    const m = mesh?.material;
    if (!(m instanceof THREE.MeshPhysicalMaterial)) return;
    for (const slot of THUMB_TEX_SLOTS) {
      const tex = (m as unknown as Record<string, unknown>)[slot];
      if (tex instanceof THREE.Texture && !graphTextureReadyForGpu(tex)) {
        invalidate();
        return;
      }
    }
  });

  return null;
}

/**
 * Captures the preview to a blob once, `delayMs` after mount, and reports it via
 * `onCaptured`. Runs entirely inside the Canvas (uses the live gl/scene/camera), so it
 * works regardless of whether a forwarded ref survives next/dynamic. The shelf uses
 * this to snapshot each settled sphere to a static <img> and free the WebGL context.
 */
function AutoCapture({
  delayMs,
  mimeType,
  quality,
  onCaptured,
}: {
  delayMs: number;
  mimeType: string;
  quality: number;
  onCaptured: (blob: Blob | null) => void;
}) {
  const { gl, scene, camera, invalidate } = useThree();
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      for (let i = 0; i < 5; i++) {
        invalidate();
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (cancelled) return;
      }
      gl.render(scene, camera);
      gl.domElement.toBlob(
        (b) => {
          if (!cancelled) onCaptured(b);
        },
        mimeType,
        mimeType === 'image/jpeg' || mimeType === 'image/webp' ? quality : undefined,
      );
    }, delayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [delayMs, mimeType, quality, onCaptured, gl, scene, camera, invalidate]);
  return null;
}

/**
 * Warms this thumbnail's shader programs via gl.compileAsync (parallel, non-blocking)
 * so the demand render / capture reuses linked programs instead of compiling them
 * synchronously inside the render frame. Retries a few times to catch the async
 * environment/texture loads. No-op without KHR_parallel_shader_compile.
 */
function ThumbShaderPrecompile() {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    patchCompileAsyncOnRenderer(gl);
    const ctx = gl.getContext?.();
    if (!ctx?.getExtension?.('KHR_parallel_shader_compile')) return;

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const compile = () => {
      if (cancelled) return;
      compileAsyncSafe(gl, scene, camera)
        .then(() => {
          if (!cancelled) invalidate();
        })
        .catch(() => {});
    };
    // Single delayed warm — avoid 0/250/700 triple compile racing dispose.
    timers.push(setTimeout(compile, 200));
    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
    };
  }, [gl, scene, camera, invalidate]);
  return null;
}

function ThumbnailCaptureBridge({
  fref,
}: {
  fref: React.Ref<MaterialSphereThumbnailHandle | null>;
}) {
  const { gl, scene, camera, invalidate } = useThree();

  useImperativeHandle(
    fref,
    () => ({
      captureToBlob: async (mimeType = 'image/png', quality?: number) => {
        for (let i = 0; i < 6; i++) {
          invalidate();
          await new Promise<void>((r) => requestAnimationFrame(() => r()));
        }
        gl.render(scene, camera);
        return new Promise((resolve) => {
          gl.domElement.toBlob(
            (b) => resolve(b),
            mimeType,
            mimeType === 'image/jpeg' ? quality ?? 0.92 : undefined,
          );
        });
      },
    }),
    [gl, scene, camera, invalidate],
  );

  return null;
}

function useMaterialOverrides(
  type: 'pbr' | 'graph',
  data: unknown,
  attributeDefs: AttributeDef[] | undefined,
): { overrides: Record<string, unknown> | null; fallback?: { color?: string; metalness?: number; roughness?: number } } {
  const pbrFallback = useMemo(() => {
    if (type !== 'pbr' || !data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    return {
      color: typeof d.color === 'string' ? d.color : '#cccccc',
      metalness: typeof d.metalness === 'number' ? d.metalness : 0,
      roughness: typeof d.roughness === 'number' ? d.roughness : 0.5,
    };
  }, [type, data]);

  const attrDefsKey = useMemo(() => JSON.stringify(attributeDefs ?? []), [attributeDefs]);

  const graphEval = useMemo((): EvalResult | null => {
    if (type !== 'graph' || !data || typeof data !== 'object') return null;
    const graph = data as MaterialGraphData;
    if (!Array.isArray(graph.nodes) || graph.nodes.length === 0) return null;
    let defs: AttributeDef[] | undefined;
    try {
      const parsed = JSON.parse(attrDefsKey || '[]') as AttributeDef[];
      defs = parsed.length ? parsed : undefined;
    } catch {
      defs = undefined;
    }
    try {
      const ctx = materialGraphUsesPerMeshUvRandomize(graph) ? { meshName: '__material_thumb__' } : undefined;
      return evaluateMaterialGraph(graph, {}, defs, ctx);
    } catch {
      return null;
    }
  }, [type, data, attrDefsKey]);

  useEffect(() => {
    return () => {
      if (graphEval) disposeMaterialGraphResult(graphEval);
    };
  }, [graphEval]);

  if (type === 'pbr' && data && typeof data === 'object') {
    return { overrides: data as Record<string, unknown>, fallback: pbrFallback };
  }

  if (type === 'graph' && graphEval) {
    return { overrides: graphEval.overrides as Record<string, unknown>, fallback: pbrFallback };
  }

  return { overrides: null, fallback: pbrFallback };
}

/**
 * Small lit studio preview of a PBR or graph material on a sphere (material strip / library).
 * Uses `frameloop="demand"` and targeted invalidation to limit GPU work when many thumbnails are visible.
 */
export const MaterialSphereThumbnail = forwardRef<
  MaterialSphereThumbnailHandle,
  MaterialSphereThumbnailProps
>(function MaterialSphereThumbnail(
  { type, data, attributeDefs, size, className, renderKey = '', autoCaptureDelayMs, onAutoCaptured },
  ref,
) {
  const { overrides, fallback } = useMaterialOverrides(type, data, attributeDefs);
  const sphereRef = useRef<THREE.Mesh>(null);
  const captureRef = useRef<MaterialSphereThumbnailHandle | null>(null);

  useImperativeHandle(ref, () => ({
    captureToBlob: (mimeType?: string, q?: number) =>
      captureRef.current?.captureToBlob(mimeType, q) ?? Promise.resolve(null),
  }));

  const bootKey = `${type}:${renderKey}`;

  const boxStyle =
    size != null
      ? { width: size, height: size }
      : { width: '100%', height: '100%', minHeight: 0 };

  return (
    <div className={className} style={boxStyle}>
      <Canvas
        className="block h-full w-full"
        shadows={false}
        frameloop="demand"
        dpr={1}
        gl={{
          alpha: false,
          antialias: false,
          powerPreference: 'low-power',
          preserveDrawingBuffer: true,
        }}
        camera={{
          position: [1.25, 0.52, 1.35],
          fov: 36,
          near: 0.08,
          far: 24,
        }}
        onCreated={({ camera, gl }) => {
          camera.lookAt(0, 0.38, 0);
          gl.debug.checkShaderErrors = false;
        }}
      >
        <ThumbnailDemandDriver sphereRef={sphereRef} bootKey={bootKey} />
        <ThumbShaderPrecompile />
        <ThumbnailCaptureBridge fref={captureRef} />
        {autoCaptureDelayMs != null && onAutoCaptured && (
          <AutoCapture
            delayMs={autoCaptureDelayMs}
            mimeType="image/webp"
            quality={0.9}
            onCaptured={onAutoCaptured}
          />
        )}
        <StudioPreview sphereRef={sphereRef} overrides={overrides} fallback={fallback} />
      </Canvas>
    </div>
  );
});

MaterialSphereThumbnail.displayName = 'MaterialSphereThumbnail';
